import React, { useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './Button';
import { Alert } from './Dialog';
import { LogIn, Shield } from 'lucide-react';

/**
 * Login component integrated with Azure AD
 */
export const Login: React.FC = () => {
  // No local form state required; authentication is handled by Azure AD
  
  const { login, isLoading, error, clearError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !isLoading) {
      const from = location.state?.from || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, isLoading, navigate, location]);

  // Main Azure AD login
  const handleAzureLogin = async () => {
    try {
      clearError();
      await login();
    } catch (err) {
      console.error('Azure AD login error:', err);
    }
  };

  // Mock login for development
  // Mock/login helpers removed — using Azure AD flow only

  return (
    <div className="min-h-screen flex items-center justify-center gradient-login-bg">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-card p-8 animate-slide-up">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mx-auto mb-4">
              <LogIn className="text-white" size={32} />
            </div>
            <h1 className="text-h2">ERP Viver Organizer</h1>
            <p className="text-slate-500 mt-2">
              Acesse com sua conta corporativa
            </p>
          </div>

          {/* Erro */}
          {error && (
            <Alert 
              variant="error" 
              className="mb-6"
              onClose={clearError}
            >
              {error}
            </Alert>
          )}

          {/* Login Principal - Azure AD */}
          <div className="space-y-4 mb-6">
            <Button
              variant="primary"
              onClick={handleAzureLogin}
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-2"
            >
              <Shield size={20} />
              {isLoading ? 'Conectando...' : 'Entrar com Conta Corporativa'}
            </Button>
            
            {/* Dica sobre popups */}
            <p className="text-xs text-gray-500 text-center px-2">
              Se a tela da Microsoft não abrir, verifique se os popups estão bloqueados no seu navegador
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-small text-slate-500">
              © 2026 Viver Organizer. Todos os direitos reservados.
            </p>
            <div className="flex justify-center gap-4 mt-3">
              <a href="#" className="text-small text-slate-400 hover:text-slate-600">
                Suporte
              </a>
              <a href="#" className="text-small text-slate-400 hover:text-slate-600">
                Política de Privacidade
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};